import React, { useState } from 'react';
import { Button } from './ui/button';
import { Share2, Download, Copy, Link as LinkIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

// Helper to generate the public share URL for social media
// The server must be configured to serve OG tags for crawlers at these URLs
export const getShareUrl = (type, id) => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  // Use production domain if on production
  const baseUrl = (hostname === 'bettingtracker.pro' || hostname === 'www.bettingtracker.pro')
    ? 'https://bettingtracker.pro'
    : process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  // Return the actual public URL (not an API endpoint)
  switch(type) {
    case 'blog':
      return `${baseUrl}/blog/${id}`;
    case 'betting-strategies':
    case 'strategy':
      return `${baseUrl}/betting-strategies/${id}`;
    case 'tip-services':
    case 'service':
      return `${baseUrl}/tip-services/${id}`;
    default:
      return `${baseUrl}/${type}/${id}`;
  }
};

// Backward compatibility alias
export const getOGShareUrl = getShareUrl;

const ShareButton = ({ elementRef, shareUrl, title = "Share", description = "Check this out!" }) => {
  const [showModal, setShowModal] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const captureAsImage = async () => {
    if (!elementRef || !elementRef.current) {
      toast.error('Element not found');
      return;
    }

    setCapturing(true);
    try {
      const canvas = await html2canvas(elementRef.current, {
        backgroundColor: '#0f172a', // slate-900
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      });

      // Convert to blob
      canvas.toBlob((blob) => {
        // Download the image
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `betbanky-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('Image downloaded!');
        setShowModal(false);
      }, 'image/png');
    } catch (error) {
      console.error('Error capturing image:', error);
      toast.error('Failed to capture image');
    } finally {
      setCapturing(false);
    }
  };

  const copyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(description);
    const url = encodeURIComponent(shareUrl || window.location.href);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(shareUrl || window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(shareUrl || window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareViaWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl || window.location.href
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      toast.error('Web Share API not supported');
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        size="sm"
        className="border-white/20 text-white hover:bg-white/10"
      >
        <Share2 className="w-4 h-4 mr-2" />
        Share
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="glass-dark border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-400" />
              Share
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Download as Image */}
            {elementRef && (
              <Button
                onClick={captureAsImage}
                disabled={capturing}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {capturing ? 'Capturing...' : 'Download as Image'}
              </Button>
            )}

            {/* Copy Link */}
            <Button
              onClick={copyLink}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>

            {/* Native Share (Mobile) */}
            {navigator.share && (
              <Button
                onClick={shareViaWebShare}
                className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share via...
              </Button>
            )}

            {/* Social Media Share Buttons */}
            <div className="pt-3 border-t border-white/10">
              <p className="text-gray-400 text-sm mb-3">Share on social media</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={shareOnTwitter}
                  className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
                  size="sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </Button>

                <Button
                  onClick={shareOnFacebook}
                  className="bg-[#4267B2] hover:bg-[#365899] text-white"
                  size="sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </Button>

                <Button
                  onClick={shareOnLinkedIn}
                  className="bg-[#0077b5] hover:bg-[#006699] text-white"
                  size="sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButton;
